import {httpDelete, httpGet, httpPost, httpPut} from "./http";
import {formatDate, roundNumberUpToPrecision, sumArr, ucFirst} from "./misc";

const chargeUrl = window.env.BACKEND_API_URL + '/charges';
const paymentUrl = window.env.BACKEND_API_URL + '/payments';
const taxUrl = window.env.BACKEND_API_URL + '/taxes';
const transactionUrl = window.env.BACKEND_API_URL + '/transactions';

//region Charge

const getCharges = async (accountID, profileID, {page = 1, perPage = 10}, signal) => {
    const queryString = `?per_page=${perPage}&page=${page}&account_id=${accountID}&profile_id=${profileID}`;
    const chargeIndexUrl = chargeUrl + queryString;

    return await httpGet(chargeIndexUrl, signal);
};

const createCharge = async (accountID, membershipID, {chargedAt, type, name, amount, taxes}, signal) => {
    const body = {};

    if (accountID) body['account_id'] = accountID;
    body['membership_id'] = membershipID;
    if (chargedAt) body['charged_at'] = chargedAt;
    if (type) body['type'] = type;
    if (name) body['name'] = name;
    if (amount != null) body['amount'] = amount;
    if (taxes && taxes.length !== 0) body['taxes'] = JSON.stringify(taxes);

    return httpPost(chargeUrl, body, signal);
};

const updateCharge = async (chargeID, status, {chargedAt, type, name, amount, taxes}, signal) => {
    const chargeUpdateUrl = `${chargeUrl}/${chargeID}`

    const body = {};

    if (status) body['status'] = status;
    if (chargedAt) body['charged_at'] = chargedAt;
    if (type) body['type'] = type;
    if (name) body['name'] = name;
    if (amount != null) body['amount'] = amount;
    if (taxes) body['taxes'] = JSON.stringify(taxes);

    return httpPut(chargeUpdateUrl, body, signal);
}

const deleteCharge = async (id, signal) => {
    const chargeDeleteUrl = `${chargeUrl}/${id}`;

    return httpDelete(chargeDeleteUrl, signal)
}

//endregion

//region Payment

const createPayment = async (accountID, {paidAt, reference, method, amount}, signal) => {
    const body = {};

    if (accountID) body['account_id'] = accountID;
    if (paidAt) body['paid_at'] = paidAt;
    if (reference) body['reference'] = reference.trim();
    if (method) body['method'] = method;
    if (amount != null) body['amount'] = amount;

    return httpPost(paymentUrl, body, signal);
};

//endregion

//region Tax

const getTaxes = async (signal) => {
    return await httpGet(taxUrl, signal);
};

//endregion
//region Transactions

const getTransactions = async (accountID, {page = 1, perPage = 10, startedAt, endedAt}, signal) => {
    startedAt = startedAt ? formatDate(startedAt) : '';
    endedAt = endedAt ? formatDate(endedAt) : '';

    const queryString = `?per_page=${perPage}&page=${page}&account_id=${accountID}&started_at=${startedAt}&ended_at=${endedAt}`;
    const transactionIndexUrl = transactionUrl + queryString;

    return await httpGet(transactionIndexUrl, signal);
};

const getTransactionType = (transaction) => {
    let type = '';
    const transactionClass = transaction.transactable_type;

    switch (transactionClass) {
        case 'App\\Models\\Billings\\Charge':
            type = transaction.amount > 0 ? 'Charge' : 'Credit';
            if (transaction.transactable.type === 'late_return') {
                type += ` #${transaction.transactable.charge_number} - Late return (${ucFirst(transaction.transactable.name)})`;
            } else {
                type += ` #${transaction.transactable.charge_number} - ${ucFirst(transaction.transactable.type)}`;
            }
            break;
        case 'App\\Models\\Billings\\Payment':
            type = transaction.amount < 0 ? 'Payment' : 'Refund';
            type += ` - ${ucFirst(transaction.transactable.method)}`;
            break;
        case 'App\\Models\\Purchases\\Purchase':
            if (transaction.transactable.item_type === 'share') {
                type = transaction.amount > 0 ? 'Share Purchase' : 'Share Sale';
            } else {
                type = transaction.amount > 0 ? 'Transfer to Deposit' : 'Transfer from Deposit';
            }
            break;
        case 'App\\Models\\Bookings\\Journey':
            // Journeys may not have associated bookings so check first.
            if (transaction.transactable.booking_numbers.length !== 0) {
                type = 'Booking #' + transaction.transactable.booking_numbers.join(', #');
            } else {
                type = 'No associated booking';
            }
            break;
        case 'App\\Models\\Promotions\\UsageCredit':
            type = 'Usage Credit';
            break;
        default:
            break;
    }
    return type;
}
//endregion
//region Logical Helpers

// Exported to ChargeCreateDrawer and ChargeEditDrawer to handle dynamic calculator for the pretax field.
const handleChargePreTaxCalc = (e, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount, taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused) => {

    // We call this function again to do the tax calculation, if undefined is passed in do not reset the ref.
    if (e !== undefined) {
        preTaxAmountRef.current = e;
        setPreTaxAmount(e);
    }

    // Loop through the array of taxes and pushing the calculated amounts.
    let taxArr = [];

    taxes.forEach(tax => {
        if (taxesApplied.current[tax.name] === true) {
            taxArr.push(Math.round(preTaxAmountRef.current * parseFloat(tax.percent) * 100) / 100);
        }
    });

    setSumOfTaxes(sumArr(taxArr));

    // Error handling to not show NaN in partner input field if user has empty string or letters.
    // Also clear the paired field if e is completely cleared (null).
    if (typeof preTaxAmountRef.current === 'number' && e !== null) {
        setPostTaxAmount(roundNumberUpToPrecision(preTaxAmountRef.current + sumArr(taxArr), 2));
    } else {
        setPostTaxAmount(null);
    }

    // Set the last focused amount input field.
    lastAmountInputFocused.current = 'preTaxAmount';
}

// Exported to ChargeCreateDrawer and ChargeEditDrawer to handle dynamic calculator for the post tax field.
const handleChargePostTaxCalc = (e, postTaxAmountRef, setPreTaxAmount, setPostTaxAmount, taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused) => {

    // We call this function again to do the tax calculation, if undefined is passed in do not reset the ref.
    if (e !== undefined) {
        postTaxAmountRef.current = e;
        setPostTaxAmount(e)
    }

    let accumulatedTaxRate = 0;

    taxes.forEach(tax => {
        if (taxesApplied.current[tax.name] === true) {
            // Instead of individually subtracting the taxes from the post tax total
            // add up all the rates together, then subtract, it is the correct way of being accurate.
            accumulatedTaxRate += parseFloat(tax.percent)
        }
    })

    let totalTaxes = Math.abs((postTaxAmountRef.current / (1 + accumulatedTaxRate)) - postTaxAmountRef.current);
    setSumOfTaxes(totalTaxes);

    // Error handling to not show NaN in partner input field if user has empty string or letters.
    // Also clear the paired field if e is completely cleared (null).
    if (typeof postTaxAmountRef.current === 'number' && e !== null){
        setPreTaxAmount(roundNumberUpToPrecision(postTaxAmountRef.current - totalTaxes, 2));
    } else {
        setPreTaxAmount(null);
    }

    // Set the last focused amount input field.
    lastAmountInputFocused.current = 'postTaxAmount';
}
//endregion

export {
    getCharges,
    createCharge,
    updateCharge,
    deleteCharge,
    createPayment,
    getTaxes,
    getTransactions,
    getTransactionType,
    handleChargePreTaxCalc,
    handleChargePostTaxCalc,
};
